import { Check, ChevronsUpDown } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

/** A single option passed to Combobox. */
export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  /** Selectable options. */
  options: ComboboxOption[];
  /** Currently selected value (controlled). Pass empty string when nothing is selected. */
  value: string;
  /** Called with the selected option's value on each pick. */
  onChange: (value: string) => void;
  /** Text shown in the trigger when no option is selected. */
  placeholder?: string;
  /** Placeholder inside the search input. */
  searchPlaceholder?: string;
  /** Disables opening the dropdown. */
  disabled?: boolean;
  /** Extra Tailwind classes on the trigger element (e.g. height / border overrides). */
  className?: string;
  /** id applied to the trigger div so a <label htmlFor> can reference it. */
  id?: string;
  /** `data-testid` on the trigger; derives `${testId}-content`, `-search-input`, `-option-${value}`. */
  testId?: string;
}

/**
 * Single-select searchable combobox built on Popover + Command primitives.
 * Uses a modal Popover so it scrolls correctly when nested inside a Dialog.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled,
  className,
  id,
  testId = 'combobox',
}: ComboboxProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  function handleSelect(optValue: string) {
    onChange(optValue);
    setOpen(false);
  }

  return (
    <Popover modal open={open} {...(!disabled && { onOpenChange: setOpen })}>
      {/* div[role="combobox"] avoids nesting <button> inside <button> */}
      <PopoverTrigger asChild>
        <div
          id={id}
          role="combobox"
          data-testid={testId}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) setOpen(true);
          }}
          className={cn(
            'border-input bg-background data-[state=open]:ring-ring flex h-10 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm focus:outline-none data-[state=open]:ring-2 data-[state=open]:ring-offset-0',
            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
            className,
          )}
        >
          {selectedLabel ? (
            <span className="truncate">{selectedLabel}</span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="text-muted-foreground ml-2 h-4 w-4 shrink-0" />
        </div>
      </PopoverTrigger>

      {/* Constrained to available viewport height so the list scrolls correctly
          even when the trigger is near the bottom of a Dialog. */}
      <PopoverContent
        className="flex max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) flex-col overflow-hidden p-0"
        align="start"
        data-testid={`${testId}-content`}
        onOpenAutoFocus={(event) => {
          // Radix's default focuses the content container, which leaves the caret
          // nowhere and the user a keystroke away from searching. Cancel it (that
          // also stops the container being scrolled into view inside a Dialog) and
          // put focus in the search box so typing filters straight away.
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <Command className="min-h-0 flex-1">
          <CommandInput
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            data-testid={`${testId}-search-input`}
          />
          <CommandList id={listId} className="min-h-0 flex-1">
            <CommandEmpty data-testid={`${testId}-empty`}>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  data-testid={`${testId}-option-${opt.value}`}
                  onSelect={() => {
                    handleSelect(opt.value);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === opt.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
